import {
  Component,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { ModalController } from '@ionic/angular';
import { BookingItemStyle } from 'src/app/interfaces/interfaces';
import { ToastService } from 'src/app/services/toast-service/toast.service';
import { UserStore } from 'src/app/stores/user.store';

@Component({
  selector: 'app-layout-preferences-modal',
  templateUrl: './layout-preferences-modal.component.html',
  styleUrls: ['./layout-preferences-modal.component.scss'],
})
export class LayoutPreferencesModalComponent implements OnInit {
  private modalController = inject(ModalController);
  private userStore = inject(UserStore);
  private toastService = inject(ToastService);

  readonly BookingItemStyle = BookingItemStyle;

  selected = signal<BookingItemStyle>(BookingItemStyle.Card);
  private initial = signal<BookingItemStyle>(BookingItemStyle.Card);
  isSaving = signal(false);

  hasChanges = computed(() => this.selected() !== this.initial());
  isApplyDisabled = computed(() => !this.hasChanges() || this.isSaving());

  ngOnInit(): void {
    const prefs = this.userStore.userPreferences();
    const raw = prefs?.userSettings?.bookingItemStyle;
    const normalized =
      raw === BookingItemStyle.Stack ? BookingItemStyle.Stack : BookingItemStyle.Card;
    this.selected.set(normalized);
    this.initial.set(normalized);
  }

  onStyleChange(event: CustomEvent<{ value: BookingItemStyle }>): void {
    const v = event.detail.value;
    if (v === BookingItemStyle.Card || v === BookingItemStyle.Stack) {
      this.selected.set(v);
    }
  }

  selectStyle(style: BookingItemStyle): void {
    if (this.isSaving()) {
      return;
    }
    this.selected.set(style);
  }

  async apply(): Promise<void> {
    const prefs = this.userStore.userPreferences();
    const settings = prefs?.userSettings;
    if (!settings) {
      await this.settingsSomethingWentWrongToast();
      return;
    }

    this.isSaving.set(true);
    try {
      const result = await this.userStore.updateUserSettings({
        ...settings,
        bookingItemStyle: this.selected(),
      });
      this.dismissModal();
    } catch {
      await this.settingsSomethingWentWrongToast();
      return;
    }
    finally {
      this.isSaving.set(false);
    }
  }

  dismissModal(): void {
    this.modalController.dismiss();
  }

  private async settingsSomethingWentWrongToast(): Promise<void> {
    await this.toastService.openErrorToastAsync('settingsSomethingWentWrongToast');
  }
}
